import React, {useState, useEffect, useCallback} from 'react';
import {
    Loading,
    Error,
    withDataProvider,
    useTranslate,
    SimpleForm,
    SaveButton,
    FormDataConsumer,
    useNotify,
    useRefresh,
    FunctionField,
    useLocale,
    Labeled,
} from 'react-admin';
import {ActivitiesField, AddressField,} from "../../components";
import qs from "qs";
import moment from "moment/moment";
import {RaBox} from "ra-compact-ui";
import RichTextInput from "ra-input-rich-text";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import {required} from "../../data/validation";
import {diffInMinutes, prettyTimeString, totalMinutes} from "../activity/utils";
import {DUTY_PERIODICITY} from "../contract/constants";


const QRList = ({permissions, dataProvider, ...props}) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const locale = useLocale();

    const params = qs.parse(props?.location?.search, {ignoreQueryPrefix: true});
    const {addressId} = params.filter ? JSON.parse(params?.filter) : {}

    const [qrOrders, setQrOrders] = useState([]);
    const [serviceReport, setServiceReport] = useState();
    const [startRealization, setStartRealization] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [showOtherOrders, setShowOtherOrders] = useState(false);
    const isFromButton = props?.location?.state?.fromButton === true;

    const loadData = useCallback(() => {
            setLoading(true);
            dataProvider.getList('orders',
                {
                    filter: {
                        dutyAddressId: addressId,
                        realizationDateFrom: moment().format('YYYY-MM-DD'),
                        realizationDateUntil: moment().format('YYYY-MM-DD'),
                        showOtherOrders: showOtherOrders,
                    },
                    pagination: {page: 1, perPage: 5},
                    sort: {field: 'id', order: 'ASC'}
                }
            ).then(({data}) => {
                if (data.length === 0) {
                    setShowOtherOrders(true);
                }

                const sortedData = [...data].sort((a, b) => {
                    const indexA = DUTY_PERIODICITY.findIndex(p => p.id === a?.duty?.periodicity);
                    const indexB = DUTY_PERIODICITY.findIndex(p => p.id === b?.duty?.periodicity);

                    if (indexB !== indexA) {
                        return indexA - indexB;
                    }

                    const totalTimeA = totalMinutes(a?.duty?.activities);
                    const totalTimeB = totalMinutes(b?.duty?.activities);

                    return totalTimeB - totalTimeA;
                });
                setQrOrders(sortedData);
                setServiceReport(sortedData[0]?.serviceReport);
                setStartRealization(sortedData[0]?.startRealizationDateTime === null);
                setLoading(false);
            })
                .catch(error => {
                    setError(error);
                    setLoading(false);
                })
        }
        , [dataProvider, addressId, showOtherOrders])

    useEffect(() => {
        loadData();
    }, [loadData]);

    if (loading) return <Loading/>;
    if (error) return <Error error={error}/>;

    if (!qrOrders || qrOrders.length === 0) {
        return <Card>
            <CardContent style={{
                textAlign: "center",
                fontSize: "120%"
            }}>
                {translate("app.message.qr_orders_missing")}
            </CardContent>
        </Card>
    } else {
        return <SimpleForm
            save={() => {
                dataProvider.updateOrdersServiceReport({
                    orderIds: qrOrders?.map(order => order.id),
                    serviceReport: serviceReport,
                    fromQr: !isFromButton,
                })
                    .then(() => {
                        notify('app.message.updated_order_service_report', 'info');
                        refresh();
                        loadData();
                    })
                    .catch(() => {
                        notify('app.message.updated_order_service_report_fail', 'warning')
                    })
            }}
            toolbar={startRealization ? <SaveButton label='ra.action.start_realization'/> :
                <SaveButton label='ra.action.end_realization'/>}
            initialValues={qrOrders}
        >
            <FormDataConsumer>
                {({formData}) => {
                    const allActivities = formData
                        ? Object.values(formData)
                            .filter(order => order?.id && order?.duty?.activities)
                            .flatMap(order => order.duty.activities)
                        : [];

                    return formData && formData[0] && <RaBox display="flex" alignItems="center" style={{gap: 20}}>
                        <RaBox style={{fontSize: "120%", fontWeight: "600"}}>
                            <AddressField source="address.address" record={formData[0].duty}/>
                        </RaBox>
                        <ActivitiesField record={allActivities}/>
                    </RaBox>
                }}
            </FormDataConsumer>

            <RaBox display="flex" alignItems="center" style={{gap: 20}} fullWidth>
                <FormDataConsumer>
                    {({formData}) => formData && formData[0]?.startRealizationDateTime && (
                        <Labeled label='resources.orders.fields.startRealizationDateTime'>
                            <FunctionField
                                source="startRealizationDateTime"
                                label="resources.orders.fields.startRealizationDateTime"
                                record={formData[0]}
                                render={r => {
                                    if (!r || !r.startRealizationDateTime) return '';
                                    const options = {hour: '2-digit', minute: '2-digit', second: '2-digit'};
                                    return new Date(r.startRealizationDateTime).toLocaleTimeString(locale, options);
                                }}
                            />
                        </Labeled>
                    )}
                </FormDataConsumer>

                <FormDataConsumer>
                    {({formData}) => formData && formData[0]?.endRealizationDateTime && (
                        <Labeled label='resources.orders.fields.endRealizationDateTime'>
                            <FunctionField
                                source="endRealizationDateTime"
                                label="resources.orders.fields.endRealizationDateTime"
                                record={formData[0]}
                                render={r => {
                                    if (!r || !r.endRealizationDateTime) return '';
                                    const options = {hour: '2-digit', minute: '2-digit', second: '2-digit'};
                                    return new Date(r.endRealizationDateTime).toLocaleTimeString(locale, options);
                                }}
                            />
                        </Labeled>
                    )}
                </FormDataConsumer>

                <FormDataConsumer>
                    {({formData}) => formData && formData[0]?.startRealizationDateTime && formData[0]?.endRealizationDateTime && (
                        <Labeled label='resources.orders.fields.diffRealizationDateTime'>
                            <FunctionField
                                source="diffRealizationDateTime"
                                label="resources.orders.fields.diffRealizationDateTime"
                                record={formData[0]}
                                render={r => {
                                    if (!r || !r.startRealizationDateTime || !r.endRealizationDateTime) return '';
                                    return prettyTimeString(diffInMinutes(r?.startRealizationDateTime, r?.endRealizationDateTime))
                                }}
                            />
                        </Labeled>
                    )}
                </FormDataConsumer>
            </RaBox>
            {!startRealization && (
                <RaBox fullWidth display="flex" style={{minHeight: 200}}>
                    <RichTextInput
                        source="serviceReport"
                        label="resources.orders.fields.serviceReport"
                        parse={value => {
                            setServiceReport(value);
                            console.log(value);
                            return value;
                        }}
                        initialValue={serviceReport}
                        required
                        validate={required}
                    />
                </RaBox>
            )}

        </SimpleForm>


    }
};

export default withDataProvider(QRList);